/**
 * Transfer WebSocket Service
 * Centralized service for patient transfer WebSocket operations
 * Eliminates code duplication across transfer implementations
 */

import { WebSocketClient, WebSocketFactory } from '@/services/core/webSocketClient';
import { WEBSOCKET_SUBSCRIPTIONS } from '@/services/resources';

export interface TransferWebSocketOptions {
  transferId: number;
  hasuraToken: string;
  onTransferComplete: (careSpecialist: string) => void;
  onError?: (error: string) => void;
  timeout?: number;
}

export class TransferWebSocketService {
  private client: WebSocketClient | null = null;
  private transferTimeout: NodeJS.Timeout | null = null;

  /**
   * Start transfer monitoring
   */
  async startTransferMonitoring(options: TransferWebSocketOptions): Promise<void> {
    const { transferId, hasuraToken, onTransferComplete, onError, timeout = 120000 } = options;

    try {
      // Create WebSocket client
      this.client = WebSocketFactory.createTransferClient(
        hasuraToken,
        onTransferComplete,
        onError
      );

      // Connect to WebSocket
      await this.client.connect();

      // Subscribe to transfer updates
      this.client.subscribe({
        id: 'transfer-subscription',
        query: WEBSOCKET_SUBSCRIPTIONS.TRANSFER_PATIENT_SUBSCRIPTION,
        variables: { transferId },
        onData: (data) => {
          if (data.pep_vw_transfer_patient_list) {
            const transferData = data.pep_vw_transfer_patient_list[0];
            if (transferData?.care_specialist) {
              onTransferComplete(transferData.care_specialist);
              this.cleanup();
            }
          }
        },
        onError: (error) => {
          console.error('Transfer subscription error:', error);
          onError?.(error?.message || 'Transfer monitoring error');
          this.cleanup();
        }
      });

      // Set transfer timeout
      this.transferTimeout = setTimeout(() => {
        console.log('Transfer timeout reached');
        onError?.('Transfer request timed out');
        this.cleanup();
      }, timeout);

    } catch (error) {
      console.error('Error starting transfer monitoring:', error);
      onError?.('Failed to start transfer monitoring');
      this.cleanup();
    }
  }

  /**
   * Stop transfer monitoring
   */
  stopTransferMonitoring(): void {
    this.cleanup();
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    if (this.transferTimeout) {
      clearTimeout(this.transferTimeout);
      this.transferTimeout = null;
    }

    if (this.client) {
      this.client.close();
      this.client = null;
    }
  }

  /**
   * Check if transfer monitoring is active
   */
  get isActive(): boolean {
    return this.client?.connected || false;
  }
}

/**
 * Singleton instance for transfer WebSocket operations
 */
export const transferWebSocketService = new TransferWebSocketService();
export default transferWebSocketService;